import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEllipsisV } from 'react-icons/fa';
import { graphqlRequest } from '../../utils/graphqlRequest';
import { REPORTED_ISSUES_QUERY } from '../../graphql/reportedIssue';
import { ADMIN_ROUTES, adminPath } from '../../constants/adminPortal';
import {
  formatDate,
  issueIdLabel,
  issueTypeLabel,
  normalizeRole,
  priorityBadge,
  roleBadgeClass,
  roleLabel,
  statusBadge,
} from '../../utils/reportedIssueDisplay';

const BASE = adminPath(ADMIN_ROUTES.SUPPORT_MANAGEMENT);

export default function SupportReportedIssues() {
  const navigate = useNavigate();
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await graphqlRequest(REPORTED_ISSUES_QUERY);
      setIssues(data?.reportedIssues || []);
    } catch (e) {
      setLoadError(e?.message || 'Failed to load reported issues');
      setIssues([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="support-reported-issues">
      <div className="smgmt-page-head">
        <h1>Reported Issues Management</h1>
        <p>Manage issues submitted through the Report Issue form</p>
      </div>

      <div className="smgmt-card">
        <div className="smgmt-table-toolbar">
          <div>
            <h2>All Reported Issues</h2>
            <p className="smgmt-muted">
              {loading ? 'Loading…' : `Showing ${issues.length} reported issue${issues.length === 1 ? '' : 's'}`}
            </p>
          </div>
        </div>
        {loadError ? <p style={{ color: '#b91c1c', marginTop: 8 }}>{loadError}</p> : null}
        <div className="smgmt-table-wrap">
          <table className="smgmt-table">
            <thead>
              <tr>
                <th>Issue ID</th>
                <th>Submitted by</th>
                <th>User role</th>
                <th>Related project</th>
                <th>Issue type</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Created date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="smgmt-muted">
                    Loading…
                  </td>
                </tr>
              ) : issues.length === 0 ? (
                <tr>
                  <td colSpan={9} className="smgmt-muted">
                    No reported issues yet.
                  </td>
                </tr>
              ) : (
                issues.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <strong>{issueIdLabel(row)}</strong>
                    </td>
                    <td>{row.submittedByName || row.submittedByEmail || '—'}</td>
                    <td>
                      <span className={roleBadgeClass(normalizeRole(row.submittedByRole))}>
                        {roleLabel(normalizeRole(row.submittedByRole))}
                      </span>
                    </td>
                    <td>{row.relatedProject?.trim() || '—'}</td>
                    <td>{issueTypeLabel(row.issueType)}</td>
                    <td>{priorityBadge(row.priority)}</td>
                    <td>{statusBadge(row.status)}</td>
                    <td>{formatDate(row.createdAt)}</td>
                    <td>
                      <button
                        type="button"
                        className="smgmt-icon-btn"
                        aria-label="Open issue"
                        onClick={() => navigate(`${BASE}/reported-issues/${row.id}`)}
                      >
                        <FaEllipsisV />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}
